import type { paths } from "../api-types.js";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Your own account: the profile page's four calls.
 */

export type Account =
  paths["/api/account"]["get"]["responses"][200]["content"]["application/json"];

export async function fetchAccount(): Promise<Account> {
  const { data, response } = await api.GET("/api/account");
  if (data === undefined) throw new Error(problem(response.status, "load your account"));
  return data;
}

export async function renameAccount(name: string): Promise<Account> {
  const { data, error, response } = await api.PATCH("/api/account", { body: { name } });
  if (data !== undefined) return data;
  // A 400 here is the server saying what is wrong with the name, in its words.
  if (response.status === 400) throw new Error(error.message);
  throw new Error(problem(response.status, "save your name"));
}

/**
 * Sets a first password (`currentPassword` null) or changes one.
 *
 * Every other browser is signed out by the server when this succeeds.
 */
export async function setPassword(input: {
  readonly currentPassword: string | null;
  readonly newPassword: string;
}): Promise<void> {
  const { error, response } = await api.PUT("/api/account/password", { body: input });
  if (response.ok) return;
  // "Your current password is not right." — the one refusal a person can fix.
  if (response.status === 400 && error !== undefined) throw new Error(error.message);
  if (response.status === 429) {
    throw new Error("Too many attempts. Wait fifteen minutes and try again.");
  }
  throw new Error(problem(response.status, "change your password"));
}

/** Ends every session, this one included. The caller is signed out after. */
export async function signOutEverywhere(): Promise<void> {
  const { response } = await api.POST("/api/account/sign-out-everywhere");
  if (!response.ok) throw new Error(problem(response.status, "sign out everywhere"));
}

/** The first-visit tour was finished or declined; it is not offered again. */
export async function markTourSeen(): Promise<void> {
  const { response } = await api.POST("/api/account/tour-seen");
  if (!response.ok)
    throw new Error(problem(response.status, "save that you saw the tour"));
}
